import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { DatePickerField } from "../components/DatePickerField";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { PreventivoBadge } from "../components/PreventivoBadge";
import { TicketMessaggi } from "../components/TicketMessaggi";
import { DocumentUploader } from "../components/DocumentUploader";
import { AttachmentGallery } from "../components/AttachmentGallery";
import { AllegatiOffertaCard } from "../components/AllegatiOffertaCard";
import { ScadenzaBanner } from "../components/ScadenzaBadge";
import { ArrowLeft, FileText, CheckCircle2, Ban, Clock, Paperclip, Upload, Download, Wrench, MapPin, Users, Pencil, Trash2, Send, X, ChevronDown, FileSignature, FileCheck2, Archive, AlertCircle, UserCheck, RefreshCw, BellRing, Phone, Handshake, UserPlus, Search, MessageCircle, Vote, Copy } from "lucide-react";
import { toast } from "sonner";
import { highlightMatch } from "../lib/highlightMatch";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "—");
const sameContatto = (a, b) => a.nome === b.nome && (a.unita || "") === (b.unita || "") && (a.telefono || "") === (b.telefono || "");

export default function PreventivoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [tecniciOpen, setTecniciOpen] = useState(false);
  const [tecniciList, setTecniciList] = useState([]);
  const [tecSearch, setTecSearch] = useState("");
  const [tecCandidates, setTecCandidates] = useState([]);
  const [p, setP] = useState(null);
  // Iter122v · stato accordion cronologia (per riga: bool aperto/chiuso)
  const [historyOpen, setHistoryOpen] = useState({});
  // Iter122r · owner = fornitore titolare del preventivo (può gestire tecnici) — dichiarato DOPO `p`
  const isOwnerFornitore = user.role === "fornitore" && p?.fornitore_id === user.fornitore_id;
  // Iter122k126 · tecnico associato = fornitore presente in tecnici_condivisi ma non owner
  const isTecnicoAssociato = user.role === "fornitore" && !isOwnerFornitore && (p?.tecnici_condivisi || []).includes(user.fornitore_id);
  const tecnicoHaAccettato = isTecnicoAssociato && (p?.tecnici_accettazione || []).some((r) => r?.fornitore_id === user.fornitore_id);
  const canManageTecnici = isAdmin || isOwnerFornitore;
  const [error, setError] = useState("");
  const [importo, setImporto] = useState("");
  const [note, setNote] = useState("");
  const [allegati, setAllegati] = useState([]);
  const [rifiutoNote, setRifiutoNote] = useState("");
  const [showRifiuta, setShowRifiuta] = useState(false);
  // Iter122aa · sondaggio delibera condòmini
  const [showSondaggio, setShowSondaggio] = useState(false);
  const [sondScadenza, setSondScadenza] = useState("");
  const [sondOggetto, setSondOggetto] = useState("");
  const [sondMessaggio, setSondMessaggio] = useState("");
  const [chiudiTicket, setChiudiTicket] = useState(true); // Iter121n · default: chiudi anche il ticket
  const [editing, setEditing] = useState(false);
  const [showSott, setShowSott] = useState(false);
  const [sottMode, setSottMode] = useState("genera");
  const [sottFiles, setSottFiles] = useState([]);
  const [sottNote, setSottNote] = useState("");
  const [sottLoading, setSottLoading] = useState(false);
  const [prorogaOpen, setProrogaOpen] = useState(false);
  const [prorogaDate, setProrogaDate] = useState("");
  const [annullaOpen, setAnnullaOpen] = useState(false);
  const [annullaNote, setAnnullaNote] = useState("");
  // Iter123k165 · Modal "Duplica su altro fornitore"
  const [duplicaOpen, setDuplicaOpen] = useState(false);
  const [duplicaFornitori, setDuplicaFornitori] = useState([]);
  const [duplicaFornSel, setDuplicaFornSel] = useState("");
  const [duplicaMotivo, setDuplicaMotivo] = useState("");
  const [duplicaBusy, setDuplicaBusy] = useState(false);
  const [duplicaSearch, setDuplicaSearch] = useState("");
  const [chiudiNote, setChiudiNote] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [editForm, setEditForm] = useState({ descrizione: "", data_scadenza: "", contatti_referenti: [], reinvia: true });
  const [contatti, setContatti] = useState([]);
  const [contattiOpen, setContattiOpen] = useState(false);

  const load = () => api.get(`/preventivi/${id}`).then((r) => setP(r.data)).catch(() => navigate("/preventivi"));

  // Iter122r · condivisione preventivo con tecnico associato (altro fornitore)
  const loadTecnici = async () => {
    try { const { data } = await api.get(`/preventivi/${id}/tecnici`); setTecniciList(data.tecnici || []); }
    catch { setTecniciList([]); }
  };
  const openTecnici = async () => { await loadTecnici(); setTecCandidates([]); setTecSearch(""); setTecniciOpen(true); };
  const searchTecnici = async (q) => {
    setTecSearch(q);
    if (!q || q.length < 2) { setTecCandidates([]); return; }
    try {
      const { data } = await api.get(`/fornitori?search=${encodeURIComponent(q)}`);
      const list = Array.isArray(data) ? data : (data?.fornitori || []);
      const already = new Set([p.fornitore_id, ...(tecniciList.map((t) => t.id))]);
      setTecCandidates(list.filter((f) => !already.has(f.id)).slice(0, 10));
    } catch { setTecCandidates([]); }
  };
  const addTecnico = async (fid) => {
    try {
      const { data } = await api.post(`/preventivi/${id}/tecnici`, { fornitore_id: fid });
      setP(data); await loadTecnici(); setTecCandidates([]); setTecSearch("");
      toast.success("Tecnico associato al preventivo");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const removeTecnico = async (fid) => {
    if (!window.confirm("Rimuovere questo tecnico dalla condivisione?")) return;
    try {
      const { data } = await api.delete(`/preventivi/${id}/tecnici/${fid}`);
      setP(data); await loadTecnici();
      toast.success("Tecnico rimosso");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const markRead = () => api.post(`/preventivi/${id}/messaggi/letti`).then((r) => setP(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startEdit = () => {
    setEditForm({
      descrizione: p.descrizione || "", data_scadenza: p.data_scadenza || "",
      contatti_referenti: p.contatti_referenti || [], reinvia: true,
    });
    if (p.condominio_id) {
      api.get(`/condomini/${p.condominio_id}/contatti`).then((r) => setContatti(r.data || [])).catch(() => setContatti([]));
    }
    setEditing(true);
  };

  const toggleEditContatto = (c) => setEditForm((f) => {
    const exists = f.contatti_referenti.some((x) => sameContatto(x, c));
    const list = exists ? f.contatti_referenti.filter((x) => !sameContatto(x, c)) : [...f.contatti_referenti, c];
    return { ...f, contatti_referenti: list };
  });

  const saveEdit = async () => {
    await act(async () => {
      await api.put(`/preventivi/${id}`, editForm);
      toast.success(editForm.reinvia ? "Richiesta aggiornata e re-inviata al fornitore" : "Richiesta aggiornata");
      setEditing(false);
    });
  };

  const removePreventivo = async () => {
    if (!window.confirm("Eliminare definitivamente questa richiesta di preventivo?")) return;
    try {
      await api.delete(`/preventivi/${id}`);
      toast.success("Richiesta eliminata");
      navigate("/preventivi");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const act = async (fn) => {
    setError("");
    try { await fn(); await load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const downloadAllegato = async (doc) => {
    if (!doc) return;
    try {
      const res = await api.get(`/files/${doc.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "preventivo"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const downloadFile = async (doc) => {
    try {
      const res = await api.get(`/files/${doc.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "documento"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const downloadRicevuta = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/preventivi/${id}/ricevuta/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ricevuta_${p?.numero || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile generare la ricevuta PDF"); }
    finally { setDownloading(false); }
  };

  const sottoscrivi = async () => {
    if (sottMode === "carica" && sottFiles.length === 0) { toast.error("Carica almeno un file del preventivo sottoscritto"); return; }
    setSottLoading(true);
    try {
      await api.post(`/preventivi/${id}/sottoscrivi`, {
        modalita: sottMode,
        documenti_ids: sottFiles.map((f) => f.id),
        note: sottNote,
      });
      toast.success("Preventivo sottoscritto e inviato al fornitore");
      setShowSott(false); setSottFiles([]); setSottNote("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSottLoading(false); }
  };

  if (!p) return <div className="text-lg">Caricamento...</div>;
  const canRispondere = user.role === "fornitore" && p.stato === "Richiesto" && !p.sottoscrizione && !p.approvato_at
    // Iter122k126 · il tecnico associato può usare il form di invio solo dopo aver accettato l'incarico
    && !(user.role === "fornitore" && p?.fornitore_id !== user.fornitore_id && (p?.tecnici_condivisi || []).includes(user.fornitore_id) && !((p?.tecnici_accettazione || []).some((r) => r?.fornitore_id === user.fornitore_id)));
  const approvatoAt = p.approvato_at || p.sottoscrizione?.sottoscritto_at;
  const isApprovato = !!approvatoAt;
  const isArchiviato = !!p.archiviato;
  // Iter122k58 · Fallback per record legacy: se il backend non ha settato `riaperto`
  // (perché il fix Iter122k55 non era live al momento del click), rileviamo la
  // riapertura leggendo l'ULTIMA azione della history. Serve a garantire che la
  // card "Valutazione/Approvazione offerta" ricompaia anche sui preventivi già
  // riaperti in precedenza.
  const _lastAction = (p.history && p.history.length > 0)
    ? String(p.history[p.history.length - 1]?.action || "").toLowerCase()
    : "";
  const isRiaperto = !!p.riaperto || (!isArchiviato && _lastAction.includes("riaperta dall'archivio"));

  return (
    <div className="max-w-3xl" data-testid="preventivo-detail">
      <Button data-testid="preventivo-back" variant="ghost" className="mb-4 text-base" onClick={() => navigate("/preventivi")}>
        <ArrowLeft className="w-5 h-5 mr-2" /> Torna ai preventivi
      </Button>

      {p.data_scadenza && !p.approvato_at && !p.archiviato && (p.stato === "Richiesto" || p.stato === "Inviato") && (
        <ScadenzaBanner
          data_scadenza={p.data_scadenza}
          label={p.stato === "Inviato" ? "Termine di risposta" : "Termine per invio preventivo"}
          testid="preventivo-scadenza-banner"
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-mono text-base font-bold text-slate-500">{p.numero}</div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <FileText className="w-8 h-8 text-brand" /> {p.fornitore_nome}
          </h1>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <PreventivoBadge p={p} />
          {p.data_scadenza && (p.in_scadenza || p.in_ritardo) && (
            <span data-testid="preventivo-detail-scadenza"
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${p.in_ritardo ? "bg-red-100 text-red-800 border-red-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
              {p.scad_msg}
            </span>
          )}
        </div>
      </div>

      {/* Iter122z · banner "Nuovi messaggi in chat" — clic per scrollare/aprire la chat */}
      {(p.messaggi_non_letti || 0) > 0 && (
        <button type="button" data-testid="preventivo-msg-alert"
          onClick={() => {
            const el = document.querySelector('[data-testid="preventivo-comunicazioni"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            const toggle = document.querySelector('[data-testid="preventivo-comunicazioni"] [data-testid="messaggi-toggle"]');
            if (toggle && toggle.getAttribute("aria-expanded") !== "true") setTimeout(() => toggle.click(), 400);
          }}
          className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-red-300 bg-red-50 hover:bg-red-100 text-left transition-colors">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-600 text-white animate-pulse shrink-0">
            <MessageCircle className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-red-800">{p.messaggi_non_letti} nuovo{p.messaggi_non_letti > 1 ? "i" : ""} messaggio{p.messaggi_non_letti > 1 ? "" : ""} in chat</div>
            <div className="text-xs text-red-700">Clicca per aprire le comunicazioni con {user.role === "fornitore" ? "lo Studio" : "il fornitore"}.</div>
          </div>
          <ChevronDown className="w-5 h-5 text-red-600 shrink-0" />
        </button>
      )}

      <Card className="p-4 border-2 mb-4">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <Info label="Fornitore" value={p.fornitore_nome} />
          <Info label="Contatti fornitore" value={[p.fornitore_telefono, p.fornitore_email].filter(Boolean).join(" · ") || "—"} phone={p.fornitore_telefono} email={p.fornitore_email} testid="prev-fornitore-contatti" />
          <Info label="Condominio" value={p.condominio_nome || "—"} />
          <Info label="Manutenzione collegata" value={p.ticket_numero || "—"} />
          <InfoCFSDI label="Codice fiscale condominio" value={p.condominio_codice_fiscale} testid="prev-cf-condominio" />
          <InfoCFSDI label="Codice SDI" value={p.condominio_codice_sdi} testid="prev-sdi-condominio" />
          <Info label="Richiesto il" value={fmt(p.created_at)} />
          <Info label="Importo proposto" value={p.importo || "—"} />
        </div>
        <div className="pt-2 border-t">
          <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">Descrizione della richiesta</div>
          <p className="text-lg text-slate-800 whitespace-pre-wrap">{p.descrizione}</p>
        </div>
        {p.allegati_richiesta && p.allegati_richiesta.length > 0 && (
          <div className="pt-3 mt-3 border-t" data-testid="preventivo-allegati-richiesta">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">Allegati della richiesta</div>
            <AttachmentGallery docs={p.allegati_richiesta} testidPrefix="allegato-richiesta" />
          </div>
        )}
        {(p.condominio_indirizzo || p.maps_url) && (
          <div className="pt-3 mt-3 border-t">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">Ubicazione</div>
            {p.condominio_indirizzo && <p className="text-base text-slate-800">{p.condominio_indirizzo}</p>}
            {p.maps_url && (
              <a data-testid="preventivo-maps-link" href={p.maps_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-1 text-brand font-semibold hover:underline">
                <MapPin className="w-5 h-5" /> Apri in Google Maps
              </a>
            )}
          </div>
        )}
        {p.contatti_referenti && p.contatti_referenti.length > 0 && (
          <div className="pt-3 mt-3 border-t" data-testid="preventivo-referenti">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">Referente/i per i contatti (sopralluoghi)</div>
            <div className="flex flex-wrap gap-2">
              {p.contatti_referenti.map((c, i) => (
                <span key={i} data-testid={`preventivo-referente-view-${i}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FBEEF3] text-brand text-base font-semibold">
                  <Users className="w-4 h-4" /> {c.nome}{c.unita ? ` · int. ${c.unita}` : ""}{c.telefono ? ` · ${c.telefono}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Iter122r · Tecnici associati */}
        {(canManageTecnici || (p.tecnici_condivisi || []).length > 0) && (
          <div className="pt-3 mt-3 border-t" data-testid="preventivo-tecnici-section">
            <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
              <div className="text-sm font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <Handshake className="w-4 h-4 text-brand" /> Tecnici associati
              </div>
              {canManageTecnici && (
                <Button size="sm" variant="outline" data-testid="preventivo-condividi-btn" className="h-8 text-xs" onClick={openTecnici}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Condividi con tecnico
                </Button>
              )}
            </div>
            {(p.tecnici_condivisi || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Nessun tecnico associato. Aggiungi un fornitore-collaboratore che potrà vedere questo preventivo (scheda, allegati, chat) per assistere il titolare.</p>
            ) : (
              <TecniciBadgeList preventivoId={id} canManage={canManageTecnici} onRemove={removeTecnico} />
            )}
          </div>
        )}
        {p.note_fornitore && (
          <div className="pt-3 mt-3 border-t">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">Note del fornitore</div>
            <p className="text-lg text-slate-800 whitespace-pre-wrap">{p.note_fornitore}</p>
          </div>
        )}
        {(() => {
          const allegOff = (p.allegati && p.allegati.length > 0) ? p.allegati : (p.allegato ? [p.allegato] : []);
          if (!allegOff.length) return null;
          return (
            <div className="pt-3 mt-3 border-t" data-testid="preventivo-allegati-offerta">
              <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2" data-testid="allegati-offerta-label">
                {allegOff.length > 1 ? `ALLEGATI DELL'OFFERTA (${allegOff.length})` : "ALLEGATI DELL'OFFERTA"}
              </div>
              <AttachmentGallery docs={allegOff} testidPrefix="download-allegato" />
            </div>
          );
        })()}
        {p.sottoscrizione && (() => {
          const docs = (p.sottoscrizione.documenti && p.sottoscrizione.documenti.length > 0)
            ? p.sottoscrizione.documenti
            : (p.sottoscrizione.documento ? [p.sottoscrizione.documento] : []);
          if (!docs.length) return null;
          return (
            <div className="pt-3 mt-3 border-t" data-testid="preventivo-sottoscrizione">
              <div className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5"><FileSignature className="w-4 h-4" /> {docs.length > 1 ? `Preventivo sottoscritto (${docs.length} file)` : "Preventivo sottoscritto"}</div>
              <p className="text-sm text-slate-500 mb-2">
                Sottoscritto da {p.sottoscrizione.sottoscritto_da} il {fmt(p.sottoscrizione.sottoscritto_at)}
                {p.sottoscrizione.modalita === "carica" ? " · file firmato caricato" : " · documento generato dall'app"}
              </p>
              <AttachmentGallery docs={docs} testidPrefix="download-sottoscrizione" accent="#15803d" />
              {/* Iter122ae · comando "Crea manutenzione da preventivo" affianco al sottoscritto */}
              {isAdmin && !isArchiviato && !p.ticket_id && (
                <Button data-testid="crea-manutenzione-da-sottoscritto" className="mt-3 h-11 bg-brand hover:bg-brand-dark"
                  onClick={() => act(() => api.post(`/preventivi/${id}/accetta`))}>
                  <Wrench className="w-5 h-5 mr-2" /> Crea manutenzione da preventivo
                </Button>
              )}
              {p.ticket_id && (
                <div className="mt-3 text-sm text-green-700 flex items-center gap-1.5" data-testid="manutenzione-collegata-hint">
                  <CheckCircle2 className="w-4 h-4" /> Manutenzione già creata: <b>{p.ticket_numero}</b>
                </div>
              )}
            </div>
          );
        })()}
      </Card>

      {/* STUDIO: il fornitore ha accettato di redigere il preventivo (Fase Incarico accettato) */}
      {isAdmin && p.incarico_accettato_at && p.stato === "Richiesto" && (
        <div data-testid="incarico-accettato-studio"
          className="mb-4 p-3 rounded-xl border-2 border-green-200 bg-green-50 text-green-800 font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 shrink-0" /> Il fornitore ha accettato di redigere il preventivo il {fmt(p.incarico_accettato_at)}.
        </div>
      )}

      {/* STUDIO: il fornitore ha trasmesso il preventivo (Fase Preventivo ricevuto) */}
      {isAdmin && p.stato === "Inviato" && !isApprovato && !isArchiviato && (
        <div data-testid="preventivo-ricevuto-studio"
          className="mb-4 p-3 rounded-xl border-2 border-green-200 bg-green-50 text-green-800 font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 shrink-0" /> Il Fornitore ha trasmesso il preventivo{p.inviato_at ? <> in data <b>{fmt(p.inviato_at)}</b></> : null}.
        </div>
      )}

      {/* Iter122w · toolbar in alto rimossa: Conferma/Sottoscrivi ora dentro card "Valutazione/Approvazione offerta" */}

      {isAdmin && showSott && (
        <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/40" data-testid="preventivo-sottoscrivi-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl font-bold flex items-center gap-2"><FileSignature className="w-5 h-5 text-brand" /> Sottoscrivi il preventivo</h3>
            <Button variant="ghost" size="icon" onClick={() => setShowSott(false)}><X className="w-5 h-5" /></Button>
          </div>
          <p className="text-sm text-slate-600 mb-3">Lo Studio sottoscrive il preventivo per accettazione. Il documento viene salvato nella pratica e inviato via email al fornitore.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <button type="button" data-testid="sott-mode-genera" onClick={() => setSottMode("genera")}
              className={`p-3 rounded-lg border-2 text-left ${sottMode === "genera" ? "border-brand bg-white ring-2 ring-brand/20" : "border-slate-200 bg-white"}`}>
              <div className="font-bold text-slate-800 flex items-center gap-2"><FileText className="w-5 h-5" /> Genera dall'app</div>
              <div className="text-sm text-slate-500">Crea un PDF di accettazione con i dati dello Studio.</div>
            </button>
            <button type="button" data-testid="sott-mode-carica" onClick={() => setSottMode("carica")}
              className={`p-3 rounded-lg border-2 text-left ${sottMode === "carica" ? "border-brand bg-white ring-2 ring-brand/20" : "border-slate-200 bg-white"}`}>
              <div className="font-bold text-slate-800 flex items-center gap-2"><Upload className="w-5 h-5" /> Carica file firmato</div>
              <div className="text-sm text-slate-500">Richiama un preventivo firmato salvato in cartella.</div>
            </button>
          </div>
          {sottMode === "carica" && (
            <div className="mb-3" data-testid="sott-uploader-wrap">
              <Label className="text-base font-bold">File firmati (PDF/immagini) — uno o più</Label>
              <div className="mt-1">
                <DocumentUploader docs={sottFiles} onChange={setSottFiles} testid="sott-uploader" />
              </div>
            </div>
          )}
          <Textarea data-testid="sott-note" value={sottNote} onChange={(e) => setSottNote(e.target.value)}
            placeholder="Note per il fornitore (opzionale)..." className="min-h-[70px] text-base mb-3" />
          <Button data-testid="sott-confirm" disabled={sottLoading} className="h-12 bg-brand hover:bg-brand-dark" onClick={sottoscrivi}>
            <Send className="w-5 h-5 mr-2" /> {sottLoading ? "Invio..." : "Sottoscrivi e invia al fornitore"}
          </Button>
        </Card>
      )}

      {isAdmin && editing && (
        <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/40" data-testid="preventivo-edit-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl font-bold">Modifica la richiesta</h3>
            <Button variant="ghost" size="icon" onClick={() => setEditing(false)}><X className="w-5 h-5" /></Button>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-base font-bold">Descrizione *</Label>
              <Textarea data-testid="preventivo-edit-descrizione" value={editForm.descrizione}
                onChange={(e) => setEditForm({ ...editForm, descrizione: e.target.value })} className="min-h-[90px] text-lg mt-1" />
            </div>
            <div>
              <Label className="text-base font-bold">Scadenza risposta (opzionale)</Label>
              <div className="mt-1">
                <DatePickerField testid="preventivo-edit-scadenza" value={editForm.data_scadenza}
                  onChange={(v) => setEditForm({ ...editForm, data_scadenza: v })} disablePast className="h-12" />
              </div>
            </div>
            <div>
              <Label className="text-base font-bold">Referente per i contatti (condòmino)</Label>
              <button type="button" data-testid="preventivo-edit-referenti-toggle" disabled={!p.condominio_id}
                onClick={() => setContattiOpen((o) => !o)}
                className="w-full h-12 flex items-center justify-between px-4 rounded-lg border-2 border-slate-300 bg-white text-base text-left disabled:opacity-50 mt-1">
                <span className={editForm.contatti_referenti.length ? "text-slate-800 font-semibold" : "text-slate-400"}>
                  {editForm.contatti_referenti.length ? `${editForm.contatti_referenti.length} referente/i selezionati` : (p.condominio_id ? "Seleziona i condòmini..." : "Nessun condominio collegato")}
                </span>
                <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${contattiOpen ? "rotate-180" : ""}`} />
              </button>
              {contattiOpen && p.condominio_id && (
                <div data-testid="preventivo-edit-referenti-list" className="mt-2 border-2 border-slate-200 rounded-lg max-h-56 overflow-auto divide-y">
                  {contatti.length === 0 ? (
                    <div className="p-3 text-base text-slate-500">Nessun condòmino in anagrafica per questo stabile.</div>
                  ) : (
                    contatti.map((c, i) => {
                      const checked = editForm.contatti_referenti.some((x) => sameContatto(x, c));
                      return (
                        <label key={i} data-testid={`preventivo-edit-referente-${i}`} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={checked} onChange={() => toggleEditContatto(c)} className="w-5 h-5 accent-brand shrink-0" />
                          <div className="min-w-0">
                            <div className="font-semibold flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              {c.nome}
                              {c.unita && <span className="text-sm text-slate-500">int. {c.unita}</span>}
                              {c.ruolo && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBEEF3] text-brand">{c.ruolo}</span>}
                            </div>
                            <div className="text-sm text-slate-500">{c.telefono || "Nessun telefono in anagrafica"}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
              {editForm.contatti_referenti.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {editForm.contatti_referenti.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FBEEF3] text-brand text-sm font-semibold">
                      <Users className="w-3.5 h-3.5" /> {c.nome}{c.telefono ? ` · ${c.telefono}` : ""}
                      <button type="button" onClick={() => toggleEditContatto(c)} aria-label="Rimuovi"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-base font-semibold text-slate-700">
              <input type="checkbox" data-testid="preventivo-edit-reinvia" checked={editForm.reinvia}
                onChange={(e) => setEditForm({ ...editForm, reinvia: e.target.checked })} className="w-5 h-5 accent-brand" />
              Notifica il fornitore della richiesta aggiornata
            </label>
            <Button data-testid="preventivo-edit-save" disabled={!editForm.descrizione.trim()}
              className="h-12 bg-brand hover:bg-brand-dark" onClick={saveEdit}>
              <Send className="w-5 h-5 mr-2" /> {editForm.reinvia ? "Salva e re-invia" : "Salva modifiche"}
            </Button>
          </div>
        </Card>
      )}

      {error && (
        <div className="mb-5 p-4 bg-red-50 border-2 border-red-200 text-red-800 rounded-lg font-semibold">{error}</div>
      )}

      {/* FORNITORE: conferma verde ricezione/sottoscrizione da parte dello Studio */}
      {user.role === "fornitore" && (p.sottoscrizione || p.approvato_at) && (
        <div data-testid="fornitore-approvato-banner"
          className="mb-4 p-4 rounded-xl border-2 border-green-300 bg-green-50 text-green-800 font-semibold flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
          <span>
            Lo Studio ha ricevuto e {p.sottoscrizione ? "sottoscritto" : "approvato"} il tuo preventivo
            {p.sottoscrizione ? ` il ${fmt(p.sottoscrizione.sottoscritto_at)}` : (p.approvato_at ? ` il ${fmt(p.approvato_at)}` : "")}.
            {p.sottoscrizione?.documento ? " Puoi scaricare il documento sottoscritto qui sotto." : ""}
          </span>
        </div>
      )}


      {/* FORNITORE: accettazione dell'incarico a redigere il preventivo */}
      {user.role === "fornitore" && (p.stato === "Richiesto" || p.stato === "Inviato") && !p.sottoscrizione && !p.approvato_at && (
        p.stato === "Inviato" ? (
          <div data-testid="preventivo-trasmesso-banner"
            className="mb-4 p-4 rounded-xl border-2 border-green-200 bg-green-50 text-green-800 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0" /> Hai trasmesso l'offerta/preventivo{p.inviato_at ? <> il <b>{fmt(p.inviato_at)}</b></> : null}.
          </div>
        ) : isTecnicoAssociato ? (
          /* Iter122k126 · UX dedicata al TECNICO ASSOCIATO (diversa da quella del titolare) */
          tecnicoHaAccettato ? (
            <div data-testid="tecnico-accettato-banner"
              className="mb-4 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-800 font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              Hai accettato l'incarico come <b className="ml-1">tecnico associato</b>. Da ora puoi affiancare il fornitore titolare {p.fornitore_nome} nella redazione del preventivo.
            </div>
          ) : (
            <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/40" data-testid="tecnico-incarico-card">
              <h3 className="font-display text-xl font-bold mb-1">Vuoi accettare l'incarico?</h3>
              <p className="text-base text-slate-600 mb-3">
                Il fornitore titolare <b>{p.fornitore_nome}</b> ti ha esteso questa richiesta di preventivo come <b>tecnico associato</b>.
                Se accetti, potrai consultare la scheda, gli allegati e la chat interna per collaborare. Se non sei interessato verrai rimosso dalla condivisione.
              </p>
              <div className="actions-row">
                <Button data-testid="tecnico-accetta-btn" className="h-12 bg-green-700 hover:bg-green-800"
                  onClick={() => act(() => api.post(`/preventivi/${id}/tecnico/accetta`))}>
                  <CheckCircle2 className="w-5 h-5 mr-2" /> Accetto l'incarico
                </Button>
                <Button data-testid="tecnico-declina-toggle" variant="outline" className="h-12 text-red-700 border-red-300"
                  onClick={() => setShowRifiuta((v) => !v)}>
                  <Ban className="w-5 h-5 mr-2" /> Non sono interessato
                </Button>
              </div>
              {showRifiuta && (
                <div className="mt-4 space-y-3">
                  <Textarea data-testid="tecnico-declina-note" value={rifiutoNote} onChange={(e) => setRifiutoNote(e.target.value)}
                    placeholder="Motivo (opzionale)" className="min-h-[70px] text-lg" />
                  <Button data-testid="tecnico-declina-conferma" className="h-12 bg-red-700 hover:bg-red-800"
                    onClick={() => act(async () => {
                      await api.post(`/preventivi/${id}/tecnico/declina`, { note: rifiutoNote });
                      // Dopo il rifiuto il tecnico è rimosso dalla condivisione → torna all'elenco preventivi
                      toast.success("Richiesta declinata");
                      navigate("/preventivi");
                    })}>
                    Conferma: non sono interessato
                  </Button>
                </div>
              )}
            </Card>
          )
        ) : p.incarico_accettato_at ? (
          <div data-testid="incarico-accettato-banner"
            className="mb-4 p-4 rounded-xl border-2 border-green-200 bg-green-50 text-green-800 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0" /> Hai accettato di redigere questo preventivo il {fmt(p.incarico_accettato_at)}. Per trasmetterlo compila i campi di seguito, e/o allega l'offerta tramite "Carica file".
          </div>
        ) : (
          <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/40" data-testid="incarico-card">
            <h3 className="font-display text-xl font-bold mb-1">Vuoi redigere questo preventivo?</h3>
            <p className="text-base text-slate-600 mb-3">
              Conferma allo Studio che ti occuperai della redazione del preventivo richiesto, oppure declina la richiesta.
            </p>
            <div className="actions-row">
              <Button data-testid="accetta-incarico-button" className="h-12 bg-green-700 hover:bg-green-800"
                onClick={() => act(() => api.post(`/preventivi/${id}/accetta-incarico`))}>
                <CheckCircle2 className="w-5 h-5 mr-2" /> Accetto di redigere il preventivo
              </Button>
              <Button data-testid="declina-incarico-toggle" variant="outline" className="h-12 text-red-700 border-red-300"
                onClick={() => setShowRifiuta((v) => !v)}>
                <Ban className="w-5 h-5 mr-2" /> Non sono interessato
              </Button>
            </div>
            {showRifiuta && (
              <div className="mt-4 space-y-3">
                <Textarea data-testid="declina-incarico-note" value={rifiutoNote} onChange={(e) => setRifiutoNote(e.target.value)}
                  placeholder="Motivo (opzionale)" className="min-h-[70px] text-lg" />
                <Button data-testid="declina-incarico-conferma" className="h-12 bg-red-700 hover:bg-red-800"
                  onClick={() => act(() => api.post(`/preventivi/${id}/declina-incarico`, { note: rifiutoNote }))}>
                  Conferma: non redigo il preventivo
                </Button>
              </div>
            )}
          </Card>
        )
      )}

      {/* FORNITORE: invia/aggiorna preventivo — Iter122y · allegati unificati sotto la stessa card */}
      {canRispondere && (
        <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/40" data-testid="rispondi-card">
          <h3 className="font-display text-xl font-bold mb-4">Invia il tuo preventivo</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-base font-bold">Importo *</Label>
              <Input data-testid="rispondi-importo" value={importo} onChange={(e) => setImporto(e.target.value)}
                placeholder="es. € 850,00" className="h-12 mt-1" />
            </div>
            <div>
              <Label className="text-base font-bold">Note (opzionale)</Label>
              <Textarea data-testid="rispondi-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Dettagli, tempistiche, condizioni..." className="min-h-[80px] text-lg mt-1" />
            </div>
            {/* Iter122y · Allegati unificati: usa AllegatiOffertaCard (max 5). Il vecchio DocumentUploader è stato rimosso. */}
            <AllegatiOffertaCard preventivo={p} user={user} onUpdate={setP} className="!p-0 !border-0 !bg-transparent" embedded />
            <Button data-testid="submit-rispondi" disabled={!importo.trim()} className="h-12 bg-green-700 hover:bg-green-800"
              onClick={() => act(() => api.post(`/preventivi/${id}/rispondi`, { importo, note }))}>
              <CheckCircle2 className="w-5 h-5 mr-2" /> Invia preventivo
            </Button>
          </div>
        </Card>
      )}

      {/* ADMIN: quadro GESTIONE — fase "Richiesto" (parità con Manutenzioni) */}
      {isAdmin && p.stato === "Richiesto" && !isArchiviato && !isApprovato && (
        <Card className="p-4 border-2 mb-4" data-testid="preventivo-gestione-card">
          <h3 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><UserCheck className="w-6 h-6" /> Gestione</h3>
          <p className="text-base text-slate-600 mb-3">Comandi per gestire la richiesta in attesa dell'offerta del fornitore.</p>
          {p.incarico_accettato_at ? (
            <div data-testid="nessuna-offerta-banner"
              className="mb-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50 text-amber-900 font-semibold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 shrink-0" /> Il fornitore ha accettato l'incarico ma non ha ancora inviato l'offerta.
            </div>
          ) : (
            <div data-testid="attesa-accettazione-banner"
              className="mb-3 p-3 rounded-lg border-2 border-slate-300 bg-slate-50 text-slate-700 font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5 shrink-0" /> In attesa che il fornitore accetti l'incarico di redigere il preventivo.
            </div>
          )}
          <div className="actions-row">
            <Button data-testid="sollecita-preventivo-button" size="sm" className="h-10 text-sm bg-brand hover:bg-brand-dark"
              onClick={() => act(async () => { await api.post(`/preventivi/${id}/sollecita`); toast.success(p.incarico_accettato_at ? "Sollecito offerta inviato al fornitore." : "Sollecito accettazione inviato al fornitore."); })}>
              <BellRing className="w-4 h-4 mr-2" /> {p.incarico_accettato_at ? "Sollecita offerta" : "Sollecita accettazione"}
            </Button>
            {/* Iter122w · Re-invia e Modifica: solo prima dell'accettazione incarico */}
            {!p.incarico_accettato_at && (
              <Button data-testid="reinvia-richiesta-button" size="sm" variant="outline" className="h-10 text-sm border-brand/40 text-brand"
                onClick={() => act(async () => { await api.post(`/preventivi/${id}/reinvia`); toast.success("Richiesta re-inviata al fornitore."); })}>
                <RefreshCw className="w-4 h-4 mr-2" /> Re-invia richiesta
              </Button>
            )}
            <Button data-testid="proroga-preventivo-button" size="sm" variant="outline" className="h-10 text-sm border-amber-500 text-amber-800"
              onClick={() => { setProrogaDate((p.data_scadenza || "").slice(0, 10)); setProrogaOpen(true); }}>
              <Clock className="w-4 h-4 mr-2" /> Proroga
            </Button>
            {!p.incarico_accettato_at && (
              <Button data-testid="segna-accettato-button" size="sm" variant="outline" className="h-10 text-sm border-green-600 text-green-800"
                onClick={() => act(async () => { await api.post(`/preventivi/${id}/segna-incarico-accettato`); toast.success("Incarico segnato come accettato."); })}>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Segna in accettato
              </Button>
            )}
            {/* Iter122u · Modifica/integra richiesta — solo prima dell'accettazione incarico (Iter122w) */}
            {!editing && !p.incarico_accettato_at && (
              <Button data-testid="preventivo-edit-toggle" size="sm" variant="outline" className="h-10 text-sm" onClick={startEdit}>
                <Pencil className="w-4 h-4 mr-2" /> Modifica / integra richiesta
              </Button>
            )}
            {/* Iter122ez · rinominato "Annulla richiesta" → "Revoca richiesta" (data-testid invariato per compat) */}
            <Button data-testid="annulla-richiesta-toggle" size="sm" variant="outline" className="h-10 text-sm text-red-700 border-red-300"
              onClick={() => setAnnullaOpen((v) => !v)}>
              <Ban className="w-4 h-4 mr-2" /> Revoca richiesta
            </Button>
            {/* Iter122w · Archivia pratica (spostato dalla sezione standalone) */}
            <Button data-testid="archivia-button" size="sm" variant="outline" className="h-10 text-sm border-slate-300 text-slate-600"
              onClick={() => { if (window.confirm("Archiviare la pratica? Non sarà più attiva ma resterà consultabile nell'Archivio.")) act(() => api.post(`/preventivi/${id}/archivia`)); }}>
              <Archive className="w-4 h-4 mr-2" /> Archivia pratica
            </Button>
            {/* Iter123k165 · Duplica su altro fornitore */}
            <Button data-testid="duplica-preventivo-button" size="sm" variant="outline"
              className="h-10 text-sm border-brand/40 text-brand hover:bg-[#FBEEF3]"
              onClick={async () => {
                setDuplicaOpen(true); setDuplicaFornSel(""); setDuplicaMotivo(""); setDuplicaSearch("");
                try {
                  const r = await api.get("/fornitori?limit=10000");
                  const list = Array.isArray(r.data) ? r.data : (r.data?.fornitori || []);
                  setDuplicaFornitori(list.filter((f) => f.id !== p.fornitore_id));
                } catch (e) { console.warn("Duplica: fornitori load failed", e); setDuplicaFornitori([]); }
              }}>
              <Copy className="w-4 h-4 mr-2" /> Duplica su altro fornitore
            </Button>
          </div>
          {annullaOpen && (
            <div className="mt-4 space-y-3" data-testid="annulla-richiesta-form">
              <Textarea data-testid="annulla-richiesta-note" value={annullaNote} onChange={(e) => setAnnullaNote(e.target.value)}
                placeholder="Motivo della revoca (opzionale)" className="min-h-[70px] text-lg" />
              <Button data-testid="annulla-richiesta-conferma" className="h-10 bg-red-700 hover:bg-red-800"
                onClick={() => act(async () => {
                  await api.post(`/preventivi/${id}/annulla`, { note: annullaNote });
                  toast.success("Richiesta revocata e archiviata. Il fornitore è stato avvisato.");
                  setAnnullaOpen(false);
                })}>
                Conferma revoca
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ADMIN: Valutazione/Approvazione offerta — Fase 3 (Preventivo ricevuto).
          Iter122k34 · attivata anche per pratiche riaperte in stato "Accettato" senza
          manutenzione collegata e senza approvazione (l'utente vuole valutare/approvare
          dopo, ad es. al termine di un sondaggio delibera condòmini).
          Iter122k55 · riappare anche su pratiche riaperte dall'archivio (`p.riaperto`)
          anche se già sottoscritte/approvate, così l'admin può ri-confermare/sottoscrivere
          con editor e ri-archiviare.
          Iter122k58 · usa `isRiaperto` (fallback su history per record legacy).
          Iter122k75 · la sezione (Sottoscrivi/Conferma preventivo con editor) resta disponibile
          anche quando il preventivo è collegato a una manutenzione (`ticket_id`) e quando
          la pratica è archiviata — su richiesta utente. I bottoni contestuali (Rifiuta,
          Elimina, Archivia) sono comunque nascosti quando non applicabili. */}
      {isAdmin && (isRiaperto || isArchiviato || (!isApprovato && (p.stato === "Inviato" || p.stato === "Accettato"))) && (
        <Card className="p-4 border-2 mb-4 bg-green-50/40" data-testid="admin-actions-card">
          <h3 className="font-display text-xl font-bold mb-2">Valutazione/Approvazione offerta{isArchiviato ? " (pratica archiviata)" : ""}</h3>
          <p className="text-base text-slate-600 mb-4">
            {isArchiviato ? (
              <>La pratica è archiviata: puoi comunque <strong>sottoscrivere</strong> o <strong>ri-confermare</strong> il preventivo con l'editor.</>
            ) : (
              <>Puoi <strong>confermare</strong> o <strong>sottoscrivere</strong> il preventivo con l'editor, oppure <strong>rifiutarlo</strong>.</>
            )}
          </p>
          <div className="actions-row">
            {/* Iter122w · Conferma preventivo (spostato dalla toolbar in alto) */}
            <Button data-testid="preventivo-sottoscrivi-toggle" variant="outline" className="h-12 border-brand text-brand"
              onClick={() => setShowSott((s) => !s)}>
              <FileSignature className="w-5 h-5 mr-2" /> {p.sottoscrizione?.documento ? "Ri-conferma" : "Conferma preventivo"}
            </Button>
            {/* Iter122w · Sottoscrivi con editor (spostato dalla toolbar in alto) */}
            <Button data-testid="preventivo-conferma-toggle" variant="outline" className="h-12 border-brand text-brand"
              onClick={() => {
                const q = new URLSearchParams();
                if (p.condominio_id) q.set("condominio_id", p.condominio_id);
                if (p.fornitore_email) q.set("fornitore_email", p.fornitore_email);
                q.set("preventivo_id", p.id);
                if (p.allegato?.id && (p.allegato.content_type || "").includes("pdf")) q.set("source_file_id", p.allegato.id);
                navigate(`/conferma-preventivo?${q.toString()}`);
              }}>
              <FileCheck2 className="w-5 h-5 mr-2" /> Sottoscrivi preventivo
            </Button>
            {!isArchiviato && !isApprovato && (
              <Button data-testid="rifiuta-toggle" variant="outline" className="h-12 text-red-700 border-red-300"
                onClick={() => setShowRifiuta((v) => !v)}>
                <Ban className="w-5 h-5 mr-2" /> Rifiuta
              </Button>
            )}
            {/* Iter122aa · Sondaggio delibera condòmini spostato in card dedicata (vedi sotto) */}
            {!isArchiviato && (
              <Button data-testid="preventivo-delete-button" variant="outline" className="h-12 text-red-700 border-red-300" onClick={removePreventivo}>
                <Trash2 className="w-5 h-5 mr-2" /> Elimina richiesta
              </Button>
            )}
            {/* Iter122w · Archivia pratica (spostato dalla sezione standalone) */}
            {!isArchiviato && (
              <Button data-testid="archivia-button" variant="outline" className="h-12 border-slate-300 text-slate-600"
                onClick={() => { if (window.confirm("Archiviare la pratica? Non sarà più attiva ma resterà consultabile nell'Archivio.")) act(() => api.post(`/preventivi/${id}/archivia`)); }}>
                <Archive className="w-5 h-5 mr-2" /> Archivia pratica
              </Button>
            )}
            {/* Iter123k168 · Duplica su altro fornitore anche in stato Inviato/Accettato/Riaperto */}
            {!isArchiviato && (
              <Button data-testid="duplica-preventivo-button-adv" variant="outline"
                className="h-12 border-brand/40 text-brand hover:bg-[#FBEEF3]"
                onClick={async () => {
                  setDuplicaOpen(true); setDuplicaFornSel(""); setDuplicaMotivo(""); setDuplicaSearch("");
                  try {
                    const r = await api.get("/fornitori?limit=10000");
                    const list = Array.isArray(r.data) ? r.data : (r.data?.fornitori || []);
                    setDuplicaFornitori(list.filter((f) => f.id !== p.fornitore_id));
                  } catch (e) { console.warn("Duplica: fornitori load failed", e); setDuplicaFornitori([]); }
                }}>
                <Copy className="w-5 h-5 mr-2" /> Duplica su altro fornitore
              </Button>
            )}
          </div>
          {showRifiuta && !isArchiviato && !isApprovato && (
            <div className="mt-4 space-y-3" data-testid="rifiuto-panel">
              <Textarea data-testid="rifiuto-note" value={rifiutoNote} onChange={(e) => setRifiutoNote(e.target.value)}
                placeholder="Motivo del rifiuto (opzionale)" className="min-h-[70px] text-lg" />
              {/* Iter121n · opzione 3b: prompt esplicito per chiudere anche il ticket collegato */}
              {p.ticket_id && (
                <label className="flex items-start gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 p-2 text-sm" data-testid="chiudi-ticket-label">
                  <input type="checkbox" data-testid="chiudi-ticket-cb"
                    checked={chiudiTicket} onChange={(e) => setChiudiTicket(e.target.checked)}
                    className="mt-0.5 w-4 h-4" />
                  <span className="text-amber-900">
                    <b>Chiudi anche la manutenzione collegata</b> ({p.ticket_numero || p.ticket_id}) e archivia entrambe le pratiche.
                    <span className="block text-xs text-amber-700 mt-0.5">Consigliato quando il rifiuto del preventivo implica l&apos;abbandono definitivo dell&apos;intervento.</span>
                  </span>
                </label>
              )}
              <Button data-testid="rifiuta-button" className="h-12 bg-red-700 hover:bg-red-800"
                onClick={() => act(async () => {
                  const r = await api.post(`/preventivi/${id}/rifiuta`, { note: rifiutoNote, chiudi_ticket_collegato: chiudiTicket });
                  if (r.data?.ticket_collegato_chiuso) toast.info("Manutenzione collegata chiusa e archiviata.");
                  return r;
                })}>
                Conferma rifiuto
              </Button>
            </div>
          )}
          {/* Iter122aa · pannello Sondaggio delibera condòmini spostato in card dedicata (vedi sotto) */}
        </Card>
      )}

      {/* ALLEGATI OFFERTA (Studio e fornitore titolare) — pratiche attive; sola lettura se chiusa/archiviata.
          Iter122y · nascosto per il fornitore in fase di risposta (già mostrato dentro "Invia il tuo preventivo") */}
      {!canRispondere && (p.stato === "Richiesto" || p.stato === "Inviato" || (p.allegati_offerta || []).length > 0) && (
        <AllegatiOffertaCard preventivo={p} user={user} onUpdate={setP} className="mb-4"
          readOnly={isArchiviato || !(p.stato === "Richiesto" || p.stato === "Inviato")} />
      )}

      {/* ADMIN: Chiudi con ricevuta — chiude la posizione e la archivia automaticamente */}
      {isAdmin && (p.stato === "Richiesto" || p.stato === "Inviato") && !isArchiviato && (
        <Card className="p-4 border-2 mb-4 bg-green-50/40" data-testid="chiudi-ricevuta-card">
          <h3 className="font-display text-2xl font-bold mb-2 flex items-center gap-2"><FileCheck2 className="w-6 h-6" /> Chiudi con ricevuta</h3>
          <p className="text-base text-slate-600 mb-3">
            Chiude la posizione generando la ricevuta di chiusura: la pratica va automaticamente in <b>archivio</b>.
          </p>
          <div className="space-y-3">
            <Textarea data-testid="chiudi-preventivo-note" value={chiudiNote} onChange={(e) => setChiudiNote(e.target.value)}
              placeholder="Note di chiusura (opzionale)" className="min-h-[80px] text-lg" />
            <Button data-testid="chiudi-preventivo-button" className="h-12 bg-green-700 hover:bg-green-800"
              onClick={() => { if (window.confirm("Chiudere la posizione con ricevuta? La pratica sarà archiviata.")) act(async () => {
                await api.post(`/preventivi/${id}/chiudi`, { note: chiudiNote });
                toast.success("Posizione chiusa con ricevuta e archiviata.");
              }); }}>
              <CheckCircle2 className="w-5 h-5 mr-2" /> Chiudi e genera ricevuta
            </Button>
          </div>
        </Card>
      )}

      {/* RICEVUTA DI CHIUSURA */}
      {p.ricevuta && (
        <Card data-testid="preventivo-ricevuta-card" className="p-4 border-2 border-green-300 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-green-800">
              <FileCheck2 className="w-7 h-7" />
              <h3 className="font-display text-2xl font-bold">Ricevuta di chiusura</h3>
            </div>
            <Button data-testid="download-ricevuta-preventivo-pdf" onClick={downloadRicevuta} disabled={downloading}
              className="h-12 px-5 text-base font-bold bg-green-700 hover:bg-green-800">
              <Download className="w-5 h-5 mr-2" /> {downloading ? "Generazione..." : "Scarica PDF"}
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Info label="N. Ricevuta" value={p.ricevuta.numero_ricevuta} />
            <Info label="Data chiusura" value={fmt(p.ricevuta.data_chiusura)} />
            <Info label="Fornitore" value={p.ricevuta.fornitore} />
            <Info label="Importo offerta" value={p.ricevuta.importo || "—"} />
          </div>
          {p.ricevuta.note && <p className="mt-3 text-lg whitespace-pre-wrap">{p.ricevuta.note}</p>}
        </Card>
      )}

      {/* RICHIESTA ANNULLATA */}
      {p.stato === "Annullato" && (
        <div data-testid="preventivo-annullato-banner"
          className="mb-4 p-4 rounded-xl border-2 border-slate-300 bg-slate-100 text-slate-700 font-semibold flex items-start gap-2">
          <Ban className="w-5 h-5 shrink-0 mt-0.5" />
          <span>
            Richiesta annullata dallo Studio{p.annullato_at ? ` il ${fmt(p.annullato_at)}` : ""} e archiviata.
            {p.note_annullamento ? <> Motivo: <i>{p.note_annullamento}</i></> : null}
          </span>
        </div>
      )}

      {/* ADMIN: preventivo approvato -> crea manutenzione (solo se preventivo generato standalone, non da ticket) */}
      {isAdmin && isApprovato && !isArchiviato && !p.ticket_id && p.stato !== "Accettato" && (
        <div className="mb-4" data-testid="preventivo-approvato-area">
          <div data-testid="preventivo-approvato-banner"
            className="p-3 rounded-t-xl border-2 border-b-0 border-green-200 bg-green-50 text-green-800 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0" /> Preventivo approvato il {fmt(approvatoAt)}.
          </div>
          <div className="p-3 rounded-b-xl border-2 border-green-200 bg-white flex flex-wrap gap-3">
            <Button data-testid="crea-manutenzione-button" className="h-12 bg-brand hover:bg-brand-dark"
              onClick={() => act(() => api.post(`/preventivi/${id}/accetta`))}>
              <Wrench className="w-5 h-5 mr-2" /> Crea Manut. da Prev.
            </Button>
            {/* Iter122w · Archivia pratica (spostato dalla sezione standalone) */}
            <Button data-testid="archivia-button" variant="outline" className="h-12 border-slate-300 text-slate-600"
              onClick={() => { if (window.confirm("Archiviare la pratica? Non sarà più attiva ma resterà consultabile nell'Archivio.")) act(() => api.post(`/preventivi/${id}/archivia`)); }}>
              <Archive className="w-5 h-5 mr-2" /> Archivia pratica
            </Button>
          </div>
        </div>
      )}

      {/* Iter122w · card "Archivia pratica" standalone rimossa: il comando è ora dentro Gestione / Valutazione / Preventivo approvato / Manutenzione collegata */}

      {/* ADMIN: riapertura pratica archiviata (se non ha generato manutenzione) */}
      {isAdmin && isArchiviato && !p.ticket_id && (
        <div className="mb-4" data-testid="riapri-area">
          <Button data-testid="riapri-button" variant="outline" className="h-11 border-slate-300"
            onClick={() => act(() => api.post(`/preventivi/${id}/riapri`))}>
            <Archive className="w-5 h-5 mr-2" /> Riapri pratica dall'archivio
          </Button>
        </div>
      )}

      {/* Iter122dj · Archivia pratica RIAPERTA — dopo la riapertura la pratica torna in
          stato "Accettato" (o simili) senza ticket collegato; nessuna delle sezioni
          contestuali mostra "Archivia pratica" in questo caso. Aggiungiamo qui il
          comando standalone così l'admin può rispedirla in archivio con un clic.
          Iter122k34 · mostrata SOLO se la pratica è già approvata: altrimenti prevale
          la sezione "Valutazione/Approvazione offerta" (che ora si attiva anche in
          questo stato riaperto). */}
      {isAdmin && !isArchiviato && !p.ticket_id && p.stato === "Accettato" && isApprovato && (
        <div className="mb-4" data-testid="archivia-riaperta-area">
          <Card className="p-3 border-2 border-slate-200 bg-slate-50 flex flex-wrap items-center gap-3">
            <div className="text-sm text-slate-600 flex-1 min-w-0">
              La pratica è stata riaperta e nessuna manutenzione risulta collegata. Puoi rimandarla in archivio quando vuoi.
            </div>
            <Button data-testid="archivia-riaperta-button" variant="outline" className="h-11 border-slate-300 text-slate-700"
              onClick={() => { if (window.confirm("Archiviare la pratica? Non sarà più attiva ma resterà consultabile nell'Archivio.")) act(() => api.post(`/preventivi/${id}/archivia`)); }}>
              <Archive className="w-5 h-5 mr-2" /> Archivia pratica
            </Button>
          </Card>
        </div>
      )}

      {/* Manutenzione collegata (dopo l'adesione) */}
      {p.stato === "Accettato" && p.ticket_id && (
        <Card className="p-4 border-2 mb-4 bg-[#FBEEF3]/50 border-[#C98AA0]" data-testid="manutenzione-collegata-card">
          <h3 className="font-display text-xl font-bold mb-2 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-brand" /> Manutenzione collegata
          </h3>
          <p className="text-base text-slate-700 mb-4">
            Preventivo accettato e affidato al fornitore. La manutenzione <strong>{p.ticket_numero}</strong> è ora gestita nella sezione Manutenzioni.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="vai-manutenzione" className="h-12 bg-brand hover:bg-brand-dark"
              onClick={() => navigate(`/segnalazioni/${p.ticket_id}`)}>
              <Wrench className="w-5 h-5 mr-2" /> Vai alla manutenzione {p.ticket_numero}
            </Button>
            {/* Iter122w · Sposta in archivio (spostato dalla sezione standalone) */}
            {!isArchiviato && (
              <Button data-testid="archivia-button" variant="outline" className="h-12 border-slate-300 text-slate-600"
                onClick={() => { if (window.confirm("Spostare la pratica in archivio? Resterà consultabile nell'Archivio.")) act(() => api.post(`/preventivi/${id}/archivia`)); }}>
                <Archive className="w-5 h-5 mr-2" /> Sposta in archivio
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Iter122aa · Sondaggio delibera condòmini — visibile in tutti gli stati attivi con condominio */}
      {isAdmin && p.condominio_id && !isArchiviato && p.stato !== "Rifiutato" && (
        <Card className="p-4 border-2 mb-4 border-indigo-200 bg-indigo-50/40" data-testid="sondaggio-delibera-card">
          <h3 className="font-display text-xl font-bold mb-2 flex items-center gap-2">
            <Vote className="w-5 h-5 text-indigo-700" /> Sondaggio delibera condòmini
          </h3>
          {p.sondaggio_delibera_id ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-indigo-900 flex-1">Sondaggio già indetto per questo preventivo. I condòmini stanno votando.</p>
              <Button data-testid="sondaggio-vai" variant="outline" className="h-11 border-indigo-400 text-indigo-700"
                onClick={() => navigate(`/raccolta-dati?rid=${p.sondaggio_delibera_id}`)}>
                Vai al sondaggio
              </Button>
            </div>
          ) : !showSondaggio ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-indigo-900 flex-1">
                Sottoponi il preventivo al voto dei condòmini (Approvo / Non approvo / Astenuto). L'esito è consultivo e va ratificato in assemblea.
              </p>
              <Button data-testid="sondaggio-delibera-toggle" className="h-11 bg-indigo-700 hover:bg-indigo-800"
                onClick={() => setShowSondaggio(true)}>
                <Vote className="w-5 h-5 mr-2" /> Indici sondaggio
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="sondaggio-panel">
              <div>
                <Label className="text-sm font-bold">Termine di voto *</Label>
                <Input data-testid="sondaggio-scadenza" type="date" value={sondScadenza}
                  onChange={(e) => setSondScadenza(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  className="h-11 mt-1" />
              </div>
              <div>
                <Label className="text-sm font-bold">Oggetto (opzionale)</Label>
                <Input data-testid="sondaggio-oggetto" value={sondOggetto} onChange={(e) => setSondOggetto(e.target.value)}
                  placeholder={`Es. Rifacimento facciata — ${p.fornitore_nome || ''}`} className="h-11 mt-1" />
              </div>
              <div>
                <Label className="text-sm font-bold">Messaggio per i condòmini (opzionale)</Label>
                <Textarea data-testid="sondaggio-messaggio" value={sondMessaggio} onChange={(e) => setSondMessaggio(e.target.value)}
                  placeholder="Testo che apparirà nella pagina di voto..." className="min-h-[80px] text-base" />
              </div>
              <div className="flex gap-2">
                <Button data-testid="sondaggio-conferma" disabled={!sondScadenza} className="h-11 bg-indigo-700 hover:bg-indigo-800"
                  onClick={() => act(async () => {
                    const r = await api.post(`/preventivi/${id}/sottoponi-a-sondaggio`, {
                      data_scadenza: sondScadenza, oggetto: sondOggetto, messaggio_ai_condomini: sondMessaggio, regola: "teste",
                    });
                    toast.success(`Sondaggio indetto: ${r.data.destinatari} condòmini invitati.`);
                    setShowSondaggio(false);
                    return r;
                  })}>
                  <Vote className="w-4 h-4 mr-1" /> Indici sondaggio ai condòmini
                </Button>
                <Button variant="outline" className="h-11" onClick={() => setShowSondaggio(false)}>Annulla</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* COMUNICAZIONI Studio <-> Fornitore — visibile in tutti gli stati; readonly se Rifiutato */}
      {p.fornitore_id && (
        <Card className="p-4 border-2 mb-4" data-testid="preventivo-comunicazioni">
          <h3 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-brand" /> Comunicazioni con {user.role === "fornitore" ? "lo Studio" : "il fornitore"}
          </h3>
          <TicketMessaggi
            endpoint={`/preventivi/${id}/messaggi`}
            messaggi={p.messaggi || []}
            role={user.role}
            onUpdate={setP}
            unreadCount={p.messaggi_non_letti || 0}
            onRead={markRead}
            readonly={p.stato === "Rifiutato"}
            fornitoreTitolareId={p.fornitore_id}
            tecniciCondivisiIds={p.tecnici_condivisi || []}
          />
        </Card>
      )}

      {/* TIMELINE — Iter122v · compatta: titolo azione + autore + data/ora. La nota (se presente) è espandibile con click. */}
      <Card className="p-4 border-2">
        <h3 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Clock className="w-6 h-6" /> Cronologia</h3>
        <ul className="divide-y divide-slate-100">
          {(p.history || []).map((h, i) => {
            const hasNote = !!(h.note && String(h.note).trim());
            const isOpen = historyOpen[i];
            return (
              <li key={i} className="py-1.5" data-testid={`preventivo-history-row-${i}`}>
                <button type="button" disabled={!hasNote}
                  onClick={() => hasNote && setHistoryOpen((s) => ({ ...s, [i]: !s[i] }))}
                  className={`w-full flex items-start gap-3 text-left ${hasNote ? "cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1" : "cursor-default"}`}>
                  <div className="w-2 h-2 rounded-full bg-brand mt-2 shrink-0" />
                  <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold text-sm text-slate-800 truncate">{h.action}</span>
                    <span className="text-xs text-slate-500">· {h.by}</span>
                    <span className="text-xs text-slate-400">· {fmt(h.at)}</span>
                    {hasNote && (
                      <span data-testid={`preventivo-history-toggle-${i}`} className="text-xs text-brand font-semibold ml-auto shrink-0">{isOpen ? "− nota" : "+ nota"}</span>
                    )}
                  </div>
                </button>
                {hasNote && isOpen && (
                  <div data-testid={`preventivo-history-note-${i}`} className="ml-5 mt-1 text-sm text-slate-600 whitespace-pre-wrap break-words border-l-2 border-slate-200 pl-2">
                    {h.note}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {/* Iter122r · Dialog Condividi con tecnico */}
      <Dialog open={tecniciOpen} onOpenChange={setTecniciOpen}>
        <DialogContent aria-describedby={undefined} data-testid="tecnici-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Handshake className="w-5 h-5 text-brand" /> Condividi il preventivo con un tecnico</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Il tecnico (un altro <b>fornitore già presente nel sistema</b>) potrà vedere la scheda, gli allegati, l&apos;offerta e la chat interna di questo preventivo — anche dopo l&apos;archiviazione.
            </p>
            <div className="rounded-lg border-2 border-slate-200 p-3 bg-slate-50">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Tecnici già associati</div>
              {tecniciList.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nessuno.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {tecniciList.map((t) => (
                    <span key={t.id} data-testid={`tecnico-badge-${t.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border-2 text-xs">
                      <UserCheck className="w-3.5 h-3.5 text-brand" /> <b>{t.ragione_sociale}</b>
                      {t.settore && <span className="text-slate-400">· {t.settore}</span>}
                      {canManageTecnici && <button className="text-red-400 hover:text-red-600 ml-1" onClick={() => removeTecnico(t.id)} data-testid={`tecnico-rem-${t.id}`}><X className="w-3 h-3" /></button>}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Cerca un fornitore da aggiungere come tecnico</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="tecnici-search" value={tecSearch} onChange={(e) => searchTecnici(e.target.value)} placeholder="Ragione sociale, settore, città..." className="h-10 pl-9" />
              </div>
              {tecSearch.length >= 2 && (
                <div className="mt-2 max-h-64 overflow-y-auto rounded border-2 border-slate-200 divide-y">
                  {tecCandidates.length === 0 ? (
                    <p className="p-3 text-xs text-slate-400">Nessun fornitore corrispondente (l&apos;attuale titolare e i tecnici già associati sono esclusi).</p>
                  ) : tecCandidates.map((c) => (
                    <button key={c.id} onClick={() => addTecnico(c.id)} className="w-full text-left px-3 py-2 hover:bg-slate-50" data-testid={`tecnico-add-${c.id}`}>
                      <div className="font-semibold text-slate-800 text-sm">{highlightMatch(c.ragione_sociale, tecSearch)}</div>
                      <div className="text-xs text-slate-500">{highlightMatch([c.settore, c.citta, c.email].filter(Boolean).join(" · ") || "—", tecSearch)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="tecnici-close" onClick={() => setTecniciOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Iter123k165 · Modal Duplica su altro fornitore */}
      <Dialog open={duplicaOpen} onOpenChange={setDuplicaOpen}>
        <DialogContent className="max-w-lg" data-testid="duplica-preventivo-dialog">
          <DialogHeader>
            <DialogTitle>Duplica su altro fornitore</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Verrà creato un nuovo preventivo verso il fornitore selezionato con la stessa richiesta,
              gli allegati e le note dell'offerta precedente. <b>Il preventivo attuale resta invariato</b> —
              potrai chiuderlo/archiviarlo separatamente.
            </p>
            <div>
              <Label className="text-sm">Nuovo fornitore</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="duplica-search" value={duplicaSearch}
                  onChange={(e) => setDuplicaSearch(e.target.value)}
                  placeholder="Cerca per ragione sociale…" className="h-10 pl-9" />
              </div>
              <div className="mt-2 border-2 border-slate-200 rounded-lg max-h-56 overflow-y-auto" data-testid="duplica-fornitori-list">
                {(() => {
                  const q = duplicaSearch.trim().toLowerCase();
                  const list = duplicaFornitori.filter((f) => !q || `${f.ragione_sociale} ${f.email}`.toLowerCase().includes(q));
                  if (list.length === 0) return <div className="p-3 text-sm text-slate-500">Nessun fornitore disponibile.</div>;
                  return list.slice(0, 50).map((f) => (
                    <button key={f.id} type="button"
                      data-testid={`duplica-fornitore-${f.id}`}
                      onClick={() => setDuplicaFornSel(f.id)}
                      className={`w-full text-left px-3 py-2 border-t border-slate-100 first:border-t-0 flex items-center gap-2 ${duplicaFornSel === f.id ? "bg-[#FBEEF3]" : "hover:bg-slate-50"}`}>
                      <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${duplicaFornSel === f.id ? "bg-brand border-brand" : "border-slate-300"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-800 truncate text-sm">{f.ragione_sociale}</div>
                        <div className="text-xs text-slate-500 truncate">{f.email}{f.categoria ? ` · ${f.categoria}` : ""}</div>
                      </div>
                    </button>
                  ));
                })()}
              </div>
            </div>
            <div>
              <Label className="text-sm">Motivo della duplicazione (facoltativo)</Label>
              <Textarea data-testid="duplica-motivo" value={duplicaMotivo}
                onChange={(e) => setDuplicaMotivo(e.target.value)}
                placeholder="Es. Cambio ragione sociale del fornitore precedente…"
                className="mt-1 min-h-[70px] text-sm" />
              <p className="text-xs text-slate-500 mt-1">Se compilato, sarà visibile nella cronologia e come nota nella nuova richiesta.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDuplicaOpen(false)} disabled={duplicaBusy}>Annulla</Button>
            <Button data-testid="duplica-conferma"
              disabled={!duplicaFornSel || duplicaBusy}
              onClick={async () => {
                setDuplicaBusy(true);
                try {
                  const r = await api.post(`/preventivi/${id}/duplica`, {
                    fornitore_id: duplicaFornSel, note_studio: duplicaMotivo,
                  });
                  toast.success(`Preventivo n. ${r.data.numero} creato per ${r.data.fornitore_nome}`);
                  setDuplicaOpen(false);
                  navigate(`/preventivi/${r.data.id}`);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Duplicazione non riuscita");
                } finally { setDuplicaBusy(false); }
              }}
              className="bg-brand hover:bg-brand-dark text-white">
              {duplicaBusy ? "Creazione…" : "Crea preventivo duplicato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={prorogaOpen} onOpenChange={setProrogaOpen}>
        <DialogContent aria-describedby={undefined} data-testid="proroga-preventivo-dialog">
          <DialogHeader><DialogTitle>Proroga richiesta preventivo</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="font-bold">Nuova scadenza</Label>
            <div className="rounded-lg border-2 border-slate-200 bg-white flex justify-center py-1" data-testid="proroga-preventivo-calendar-wrap">
              <Calendar mode="single" locale={itLocale}
                selected={prorogaDate ? new Date(prorogaDate) : undefined}
                onSelect={(d) => setProrogaDate(d ? d.toISOString().slice(0, 10) : "")}
                disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }} />
            </div>
            {prorogaDate && (
              <p className="text-sm text-slate-600" data-testid="proroga-preventivo-selected">
                Data selezionata: <b>{new Date(prorogaDate).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</b>
              </p>
            )}
            <p className="text-sm text-slate-500">
              Il fornitore riceverà nuovamente la richiesta con la nuova scadenza e il contatore dei solleciti verrà azzerato.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProrogaOpen(false)} className="h-11">Annulla</Button>
            <Button data-testid="proroga-preventivo-confirm" className="h-11 bg-brand hover:bg-brand-dark" disabled={!prorogaDate}
              onClick={() => act(async () => {
                await api.post(`/preventivi/${id}/proroga`, { data_scadenza: prorogaDate });
                toast.success("Richiesta prorogata e fornitore avvisato.");
                setProrogaOpen(false);
              })}>
              Conferma proroga
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Iter122r · lista badge tecnici associati mostrata nella scheda
function TecniciBadgeList({ preventivoId, canManage, onRemove }) {
  const [tecnici, setTecnici] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get(`/preventivi/${preventivoId}/tecnici`).then((r) => { if (alive) setTecnici(r.data.tecnici || []); }).catch(() => {});
    return () => { alive = false; };
  }, [preventivoId]);
  if (!tecnici.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="preventivo-tecnici-list">
      {tecnici.map((t) => (
        <span key={t.id} data-testid={`preventivo-tecnico-${t.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FBEEF3] text-brand text-sm font-semibold">
          <UserCheck className="w-3.5 h-3.5" /> {t.ragione_sociale}
          {t.settore && <span className="text-slate-500 font-normal ml-1">· {t.settore}</span>}
          {canManage && (
            <button className="ml-1 text-red-400 hover:text-red-600" onClick={() => onRemove(t.id)} data-testid={`preventivo-tecnico-rm-${t.id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

function Info({ label, value, phone, email, testid }) {
  const isPhone = !!phone && phone !== "—";
  const isEmail = !!email && email !== "—";
  const cleanPhone = isPhone ? String(phone).split(" / ")[0].trim() : "";
  return (
    <div data-testid={testid}>
      <div className="text-sm font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg text-slate-800">
        {isPhone && isEmail ? (
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <a href={`tel:${cleanPhone}`} className="inline-flex items-center gap-1 text-brand font-semibold hover:underline" data-testid={testid ? `${testid}-tel` : undefined}>
              <Phone className="w-4 h-4" /> {phone}
            </a>
            <span className="text-slate-400">·</span>
            <a href={`mailto:${email}`} className="text-brand font-semibold hover:underline break-all">{email}</a>
          </span>
        ) : isPhone ? (
          <a href={`tel:${cleanPhone}`} className="inline-flex items-center gap-1 text-brand font-semibold hover:underline" data-testid={testid ? `${testid}-tel` : undefined}>
            <Phone className="w-5 h-5" /> {value}
          </a>
        ) : isEmail ? (
          <a href={`mailto:${email}`} className="text-brand font-semibold hover:underline break-all">{value}</a>
        ) : value}
      </div>
    </div>
  );
}

function InfoCFSDI({ label, value, testid }) {
  const missing = !value || !String(value).trim();
  return (
    <div data-testid={testid}>
      <div className="text-sm font-bold uppercase tracking-wide text-slate-500">{label}</div>
      {missing ? (
        <div className="text-lg font-bold text-red-600 uppercase tracking-wide" data-testid={testid ? `${testid}-missing` : undefined}>Inserire</div>
      ) : (
        <div className="text-lg text-slate-800 font-semibold">{value}</div>
      )}
    </div>
  );
}
